import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { RSVPStats } from '../components/admin/RSVPStats';
import { RSVPCard } from '../components/admin/RSVPCard';
import { Button } from '../components/ui/button';
import { Users, Loader2, Download, LogOut, Plane } from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { adminApi, escapeCsvValue, downloadCsv } from '../lib/adminApi';

export const Admin = () => {
  const { verified, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRSVPs = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.get('/api/rsvp');
      setRsvps(data);
      setError(null);
    } catch {
      setError('Failed to load RSVPs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (verified) fetchRSVPs();
  }, [verified, fetchRSVPs]);

  const stats = useMemo(() => ({
    total: rsvps.length,
    attending: rsvps.filter(r => r.attending === 'yes').length,
    declining: rsvps.filter(r => r.attending === 'no').length,
    day1: rsvps.filter(r => r.attendingDays?.includes('day1')).length,
    day2: rsvps.filter(r => r.attendingDays?.includes('day2')).length,
    day3: rsvps.filter(r => r.attendingDays?.includes('day3')).length
  }), [rsvps]);

  const handleDeleteRSVP = async (rsvpId, guestName) => {
    if (!window.confirm(`Are you sure you want to delete the RSVP from ${guestName}? This action cannot be undone.`)) return;
    try {
      await adminApi.delete(`/api/rsvp/${rsvpId}`);
      await fetchRSVPs();
    } catch (err) {
      alert(`Failed to delete RSVP: ${err.response?.data?.detail || err.message}`);
    }
  };

  const exportToCSV = () => {
    if (rsvps.length === 0) {
      alert('No RSVPs to export');
      return;
    }
    const headers = [
      'First Name', 'Last Name', 'Email', 'Phone', 'Attending',
      'Days Attending', 'Bringing Kids', 'Number of Kids',
      'Dietary Restrictions', 'Dietary Details', 'Message', 'Submitted At', 'Main Guest ID'
    ];
    const rows = [headers.join(',')];
    rsvps.forEach(rsvp => {
      rows.push([
        escapeCsvValue(rsvp.firstName),
        escapeCsvValue(rsvp.lastName),
        escapeCsvValue(rsvp.email),
        escapeCsvValue(rsvp.phone || ''),
        escapeCsvValue(rsvp.attending),
        escapeCsvValue(rsvp.attendingDays?.join('; ') || ''),
        rsvp.bringingKids ? 'Yes' : 'No',
        rsvp.numberOfKids || 0,
        escapeCsvValue(rsvp.dietaryRestrictions || ''),
        escapeCsvValue(rsvp.dietaryDetails || ''),
        escapeCsvValue(rsvp.message || ''),
        escapeCsvValue(new Date(rsvp.submittedAt).toLocaleString()),
        escapeCsvValue(rsvp.mainGuestId || 'Main Guest')
      ].join(','));
    });
    downloadCsv(rows, `wedding-rsvps-${new Date().toISOString().split('T')[0]}.csv`);
  };

  if (!verified || loading) {
    return (
      <div className="min-h-screen cream-bg">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-12 h-12 blue-text animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen cream-bg">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-red-600 text-xl">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cream-bg">
      <Navbar />

      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Users className="w-12 h-12 blue-text mx-auto mb-6" />
            <h1 className="font-serif text-5xl md:text-6xl font-semibold blue-text mb-4">
              RSVP Dashboard
            </h1>
            <p className="font-sans text-lg blue-text opacity-80">
              Wedding Guest Responses
            </p>
          </div>

          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              type="button"
              onClick={() => navigate('/admin/travel')}
              variant="outline"
              className="border-2 border-primary blue-text hover:bg-blue-50"
              data-testid="travel-forms-nav-btn"
            >
              <Plane className="w-4 h-4 mr-2" />
              Travel Forms
            </Button>
            <Button
              type="button"
              onClick={exportToCSV}
              disabled={rsvps.length === 0}
              className="blue-bg text-white hover:opacity-90 flex items-center gap-2"
              data-testid="export-rsvp-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export to CSV
            </Button>
            <Button
              type="button"
              onClick={logout}
              variant="outline"
              className="border-2 border-primary blue-text hover:bg-blue-50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RSVPStats stats={stats} />
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {rsvps.map((rsvp) => (
              <RSVPCard key={rsvp.id} rsvp={rsvp} onDelete={handleDeleteRSVP} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
